import { Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ChartDataSets, ChartOptions } from 'chart.js';
import { Label } from 'ng2-charts';
import { Company, NewListProductsRequest, ProductWorkflow, ProductWorkflowState, ProductWorkflowStateReportData, ProductWorkflowStateReportRequest, Release, SituViewWebApiClient, WorkflowStateType, XAxisType, YAxisType } from 'situview-webapi-client';
import { AuthService, BreadcrumbService, CompanyViewService, ProductService, ReleaseService, StateService, ThemeService, Utils } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

@Component({
  selector: 'theme-product-progress-report',
  templateUrl: './theme-product-progress-report.component.html',
  styleUrls: ['./theme-product-progress-report.component.less'],
})
export class ThemeProductProgressReportComponent extends SituBaseComponent {

barChartOptions: ChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    xAxes: [
      {
        stacked: true,
        scaleLabel: {
          display: false,
          labelString: ''
        }
      }
    ],
    yAxes: [
      {
        stacked: true,
        scaleLabel: {
          display: false,
          labelString: ''
        }
      }
    ]
  }
};

  graphStackCurrentIndex = -1;
  graphStack: GraphData[] = [];

  productWorkflowStates: Map<string, string> = new Map<string, string>();
  productWorkflowColours: Map<string, string> = new Map<string, string>();

  private _loading = false;
  
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private productService: ProductService,
    private themeService: ThemeService,
    private situViewClient: SituViewWebApiClient,
    private companyViewService: CompanyViewService,
    private releaseService: ReleaseService,
    private breadcrumbService: BreadcrumbService,
    private route: ActivatedRoute,
    private router: Router) {
    super(authService, stateService);

    this.load();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  private async load() {
    await this.loadProductWorkflowStates();

    var params = this.route.snapshot.queryParams;
    var xAxisType = XAxisType[params['xAxisType'] as keyof typeof XAxisType];
    var yAxisType = YAxisType[params['yAxisType'] as keyof typeof YAxisType];
    var releaseCompanyIdFilter = params['releaseCompanyIdFilter'] as string;
    var releaseIdFilter = params['releaseIdFilter'] as string;
    var themeCompanyIdFilter = params['themeCompanyIdFilter'] as string;
    var themeIdFilter = params['themeIdFilter'] as string;
    var themeIsInReleaseFilter = params['themeIsInReleaseFilter'] === 'false' ? false : true;
    var subjectName = params['subjectName'] as string;

    await this.loadGraphData(
      xAxisType, yAxisType,
      releaseCompanyIdFilter,
      releaseIdFilter,
      themeCompanyIdFilter,
      themeIdFilter,
      themeIsInReleaseFilter,
      subjectName
    );
  }

  private async loadProductWorkflowStates() {
    var configs = await this.productService.LoadProductWorkflowConfiguration();

    var stateTypeCountList = new Map<WorkflowStateType, number>();
    
    for (var config of configs!) {
      var state = ProductWorkflowState[config.state!].toLowerCase();
      this.productWorkflowStates.set(state, config.name!);

      var stateTypeCount = stateTypeCountList.get(config.stateType!) ?? -1;
      stateTypeCount++;
      stateTypeCountList.set(config.stateType!, stateTypeCount);

      switch(config.stateType) {
        case WorkflowStateType.Start: {
            this.productWorkflowColours.set(state, `hsl(248, 56%, ${56 + this.colourWorkflowStateTypeOffset(stateTypeCount)}%)`);
            continue;
        }
        case WorkflowStateType.Completed: {
            this.productWorkflowColours.set(state, `hsl(169, 100%, ${40 + this.colourWorkflowStateTypeOffset(stateTypeCount)}%)`);
            continue;
        }
        case WorkflowStateType.Paused:
        case WorkflowStateType.Retired: {
            this.productWorkflowColours.set(state, `hsl(40, 81%, ${55 + this.colourWorkflowStateTypeOffset(stateTypeCount)}%)`);
            continue;
        }
        case WorkflowStateType.Failed: {
            this.productWorkflowColours.set(state, `hsl(349, 97%, ${64 + this.colourWorkflowStateTypeOffset(stateTypeCount)}%)`);
            continue;
        }
        default: {
            this.productWorkflowColours.set(state, `hsl(208, 79%, ${51 + this.colourWorkflowStateTypeOffset(stateTypeCount)}%)`);
            continue;
        }
      }

    }
  }

  private colourWorkflowStateTypeOffset(stateTypeCount: number): number {
    // Return results like the following
    // stateTypeCount = 0, result = 0
    // stateTypeCount = 1, result = -5
    // stateTypeCount = 2, result = 5
    // stateTypeCount = 3, result = -10
    // stateTypeCount = 4, result = 10

    return stateTypeCount % 2 === 0 ? stateTypeCount * 2.5 : -((stateTypeCount + 1) * 2.5);
  }

  private async loadGraphData(
    xAxisType = XAxisType.ReleaseCompanies, yAxisType = YAxisType.ProductStatus,
    releaseCompanyIdFilter?: string, releaseIdFilter?: string, themeCompanyIdFilter?: string, themeIdFilter?: string,
    themeIsInReleaseFilter = true, subjectName?: string) {
    this.loading = true;

    try {
      var newIndex = this.graphStackCurrentIndex + 1;

      var request = new ProductWorkflowStateReportRequest();
      request.xAxisType = xAxisType;
      request.yAxisType = yAxisType;
      request.releaseCompanyIdFilter = releaseCompanyIdFilter;
      request.releaseIdFilter = releaseIdFilter;
      request.themeCompanyIdFilter = themeCompanyIdFilter;
      request.themeIdFilter = themeIdFilter;
      request.themeIsInReleaseFilter = themeIsInReleaseFilter;

      this.updateParams(request, subjectName);

      var resultPromise = this.situViewClient.productWorkflowStateReport(request);

      var xAxisName: string | undefined = undefined;
      var xAxisDataTypeName: string | undefined = undefined;
      var xLabelMapping: Map<string, string>;
      var alternativeViewTitle: string | undefined = undefined;
      var alternativeViewFunc: (() => any) | undefined = undefined;
      if (xAxisType === XAxisType.ReleaseCompanies) {
        xLabelMapping = await this.loadCompanies();
        xAxisName = 'Companies';
        xAxisDataTypeName = 'Company';

        alternativeViewTitle = this.buildGraphTitle(XAxisType.ThemeCompanies, releaseCompanyIdFilter, releaseIdFilter, themeCompanyIdFilter, themeIdFilter, false, subjectName);
        alternativeViewFunc = () => this.loadGraphData(XAxisType.ThemeCompanies, YAxisType.ProductStatus, releaseCompanyIdFilter, releaseIdFilter, themeCompanyIdFilter, themeIdFilter, false, subjectName);
      } else if (xAxisType === XAxisType.Release) {
        xAxisName = 'Releases';
        xAxisDataTypeName = 'Release';
        xLabelMapping = await this.loadReleases(releaseCompanyIdFilter!);
      } else if (xAxisType === XAxisType.ThemeCompanies) {
        xAxisName = 'Companies';
        xAxisDataTypeName = 'Company';
        xLabelMapping = await this.loadCompanies();
      } else if (xAxisType === XAxisType.Theme) {
        xAxisName = 'Themes';
        xAxisDataTypeName = 'Theme';
        xLabelMapping = await this.loadThemes();

        alternativeViewTitle = this.buildGraphTitle(XAxisType.Supplier, releaseCompanyIdFilter, releaseIdFilter, themeCompanyIdFilter, themeIdFilter, themeIsInReleaseFilter, subjectName);
        alternativeViewFunc = () => this.loadGraphData(XAxisType.Supplier, YAxisType.ProductStatus, releaseCompanyIdFilter, releaseIdFilter, themeCompanyIdFilter, themeIdFilter, themeIsInReleaseFilter, subjectName);
      } else if (xAxisType === XAxisType.Supplier) {
        xAxisName = 'Product Suppliers';
        xAxisDataTypeName = 'Product Supplier';
        xLabelMapping = await this.loadCompanies();
      } else {
        xLabelMapping = new Map<string, string>();
      }

      var title = this.buildGraphTitle(xAxisType, releaseCompanyIdFilter, releaseIdFilter, themeCompanyIdFilter, themeIdFilter, themeIsInReleaseFilter, subjectName);

      var yAxisName = "Products Count";
      var yAxisDataTypeName = "Product State";

      var result = await resultPromise;
      if (!result.success) {
        this.showMessage("Failed to load Product Workflow State Report Data.");
        console.log(result.errorMessages);
        return;
      }

      var data = new GraphData(request, result.data!, subjectName, title, xAxisName, yAxisName, xAxisDataTypeName, yAxisDataTypeName);
      data.alternativeViewTitle = alternativeViewTitle;
      data.alternativeViewFunc = alternativeViewFunc;

      data.buildGraphData(xLabelMapping, this.productWorkflowStates, this.productWorkflowColours);

      this.graphStackCurrentIndex = newIndex;
      this.graphStack.push(data);

      if (xAxisName) {
        var scaleLabel = this.barChartOptions.scales!.xAxes![0].scaleLabel;
        scaleLabel!.display = true;
        scaleLabel!.labelString = xAxisName;
      }
      if (yAxisName) {
        var scaleLabel = this.barChartOptions.scales!.yAxes![0].scaleLabel;
        scaleLabel!.display = true;
        scaleLabel!.labelString = yAxisName;
      }

      this.barChartOptions = { ...this.barChartOptions }; // force change detection

    } finally {
      this.loading = false;
    }
    
  }

  updateParams(request: ProductWorkflowStateReportRequest, subjectName?: string) {
    const queryParams: { [key: string]: any } = {};

    if (request.xAxisType) {
      queryParams.xAxisType = XAxisType[request.xAxisType];
    }
    if (request.yAxisType) {
      queryParams.yAxisType = YAxisType[request.yAxisType];
    }
    if (request.releaseCompanyIdFilter) {
      queryParams.releaseCompanyIdFilter = request.releaseCompanyIdFilter;
    }
    if (request.releaseIdFilter) {
      queryParams.releaseIdFilter = request.releaseIdFilter;
    }
    if (request.themeCompanyIdFilter) {
      queryParams.themeCompanyIdFilter = request.themeCompanyIdFilter;
    }
    if (request.themeIdFilter) {
      queryParams.themeIdFilter = request.themeIdFilter;
    }
    if (request.themeIsInReleaseFilter) {
      queryParams.themeIsInReleaseFilter = request.themeIsInReleaseFilter;
    }
    if (subjectName) {
      queryParams.subjectName = subjectName;
    }

    const urlTree = this.router.createUrlTree([], {
      queryParamsHandling: "merge",
      preserveFragment: true,
      queryParams: queryParams
    });

    if (!request.xAxisType) {
      delete urlTree.queryParams['xAxisType'];
    }
    if (!request.yAxisType) {
      delete urlTree.queryParams['yAxisType'];
    }
    if (!request.releaseCompanyIdFilter) {
      delete urlTree.queryParams['releaseCompanyIdFilter'];
    }
    if (!request.releaseIdFilter) {
      delete urlTree.queryParams['releaseIdFilter'];
    }
    if (!request.themeCompanyIdFilter) {
      delete urlTree.queryParams['themeCompanyIdFilter'];
    }
    if (!request.themeIdFilter) {
      delete urlTree.queryParams['themeIdFilter'];
    }
    if (!request.themeIsInReleaseFilter) {
      delete urlTree.queryParams['themeIsInReleaseFilter'];
    }
    if (!subjectName) {
      delete urlTree.queryParams['subjectName'];
    }
  
    this.router.navigateByUrl(urlTree);
  }

  buildGraphTitle(xAxisType: XAxisType,
    releaseCompanyIdFilter?: string, releaseIdFilter?: string, themeCompanyIdFilter?: string, themeIdFilter?: string,
    themeIsInReleaseFilter = true, subjectName?: string): string {
    var title: string = "";
    if (xAxisType === XAxisType.ReleaseCompanies) {
      return "Companies With Releases";
    } else if (xAxisType === XAxisType.Release) {
      title = "Releases";
    } else if (xAxisType === XAxisType.Theme) {
      title = "Themes";
    } else if (xAxisType === XAxisType.ThemeCompanies) {
      title = "Companies With Themes";
    } else if (xAxisType === XAxisType.Supplier) {
      title = "Product Suppliers";
    }

    if (releaseCompanyIdFilter) {
      title += ` for ${subjectName}`;
    } else if (releaseIdFilter) {
      title += ` in the ${subjectName} Release`;
    } else if (themeCompanyIdFilter) {
      title += ` for ${subjectName}`;
    } else if (themeIdFilter) {
      title += ` in the ${subjectName} Theme`;
    }
    
    return title;
  }

  private async loadCompanies(): Promise<Map<string, string>> {
    var allCompanies = await this.companyViewService.LoadCompanies();
    var map = new Map<string, string>(
      allCompanies.map(x => [x.id!.toLowerCase(), x.name!])
    );
    return map;
  }
  
  private async loadReleases(company: string): Promise<Map<string, string>> {
    var releases = await this.releaseService.loadReleases(company);
    if (releases) {
      var map = new Map<string, string>(
        releases.map(x => [x.id!.toLowerCase(), x.name!])
      );
      return map;
    }
    return new Map<string, string>();
  }
  
  private async loadThemes(): Promise<Map<string, string>> {
    var themes = await this.themeService.LoadThemes();
    if (themes) {
      var map = new Map<string, string>(
        themes.map(x => [x.id!.toLowerCase(), x.name!])
      );
      return map;
    }
    return new Map<string, string>();
  }

  onBackClick() {
    if (this.graphStackCurrentIndex <= 0) {
      return;
    }

    this.graphStackCurrentIndex--;
    this.graphStack.pop();

    var currentGraph = this.graphStack[this.graphStackCurrentIndex];

    if (currentGraph.xAxisName) {
      var scaleLabel = this.barChartOptions.scales!.xAxes![0].scaleLabel;
      scaleLabel!.display = true;
      scaleLabel!.labelString = currentGraph.xAxisName;
    }
    if (currentGraph.yAxisName) {
      var scaleLabel = this.barChartOptions.scales!.yAxes![0].scaleLabel;
      scaleLabel!.display = true;
      scaleLabel!.labelString = currentGraph.yAxisName;
    }
    this.barChartOptions = { ...this.barChartOptions }; // force change detection

    this.updateParams(currentGraph.request, currentGraph.subjectName);
  }

  loadNextGraph(currentXAxisType: XAxisType, idFilter?: string, themeIsInReleaseFilter = true, subjectName?: string) {
    if (currentXAxisType === XAxisType.ReleaseCompanies) {
      this.loadGraphData(XAxisType.Release, YAxisType.ProductStatus, idFilter, undefined, undefined, undefined, themeIsInReleaseFilter, subjectName);
    } else if (currentXAxisType === XAxisType.Release) {
      this.loadGraphData(XAxisType.Theme, YAxisType.ProductStatus, undefined, idFilter, undefined, undefined, themeIsInReleaseFilter, subjectName);
    }  else if (currentXAxisType === XAxisType.ThemeCompanies) {
      this.loadGraphData(XAxisType.Theme, YAxisType.ProductStatus, undefined, undefined, idFilter, undefined, themeIsInReleaseFilter, subjectName);
    } else if (currentXAxisType === XAxisType.Theme) {
      this.loadGraphData(XAxisType.Supplier, YAxisType.ProductStatus, undefined, undefined, undefined, idFilter, themeIsInReleaseFilter, subjectName);
    }
  }

  onGraphClicked(e: any): void {
    if (!e.active?.length) return;

    var chartElement = e.active[0];
    var dataIndex = chartElement._index;

    var currentGraph = this.graphStack[this.graphStackCurrentIndex];
    var currentXAxisType = currentGraph.request.xAxisType;
    var [xKey, xValue] = currentGraph.xKeyLookup[dataIndex];
    var themeIsInReleaseFilter = currentGraph.request.themeIsInReleaseFilter

    if (currentXAxisType) {
      this.loadNextGraph(currentXAxisType, xKey, themeIsInReleaseFilter, xValue);
    }
  }

  async onDownloadDataClick() {
    this.loading = true;

    try {
      
      var currentGraph = this.graphStack[this.graphStackCurrentIndex];

      var request = currentGraph.request;
      request.includeDataPoints = true;

      var result = await this.situViewClient.productWorkflowStateReport(request);
      if (!result.success) {
        this.showMessage("Failed to load Product Workflow State Report Data.");
        console.log(result.errorMessages);
        return;
      }

      currentGraph.data = result.data!;

      var allProductIds = new Set<string>();
      for (var data of result.data!) {
        for (var dataPoint of data.dataPoints!) {
          allProductIds.add(dataPoint);          
        }
      }

      var productRequest = new NewListProductsRequest();
      productRequest.filterProductIds = Array.from(allProductIds);
      var products = await this.situViewClient.newListProducts(productRequest);

      var allCompanies = await this.companyViewService.LoadCompanies();

      var dataRows: any[][] = [[currentGraph.xAxisDataTypeName ?? 'X', currentGraph.yAxisDataTypeName ?? 'Y', 'Product Company', 'Product Name', 'Product Code', 'Product ID']];
      for (var data of result.data!) {
        var dataXLower = data.x.toLowerCase();
        var dataYLower = data.y.toLowerCase();
        var xValue = currentGraph.xKeyLookup.find(([key, _]) => key === dataXLower);
        var yValue = currentGraph.yKeyLookup.find(([key, _]) => key === dataYLower);
        for (var dataPoint of data.dataPoints!) {
          var product = products.items?.find(x => x.id === dataPoint);
          var company = allCompanies.find(x => x.id === product?.companyId);
          dataRows.push([
            xValue?.[1] ?? data.x,
            yValue?.[1] ?? data.y,
            company?.name ?? '',
            product?.name ?? '',
            product?.sku ?? '',
            dataPoint
          ]);          
        }
      }

      Utils.exportToCsv(`${currentGraph.title ?? "Release Progress"}.csv`, dataRows);
      
    } finally {
      this.loading = false;
    }

  }

  totalProductCount() : number {
    return 0;
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

class GraphData {
  request: ProductWorkflowStateReportRequest;
  data: ProductWorkflowStateReportData[];

  barChartData: ChartDataSets[] = [];
  barChartLabels: Label[] = [];

  xKeyLookup: [string, string][] = [];
  yKeyLookup: [string, string][] = [];

  xAxisName?: string;
  yAxisName?: string;

  xAxisDataTypeName?: string;
  yAxisDataTypeName?: string;

  alternativeViewTitle?: string;
  alternativeViewFunc: (() => any) | undefined;

  subjectName?: string;
  title?: string;

  constructor(request: ProductWorkflowStateReportRequest, data: ProductWorkflowStateReportData[], subjectName?: string, title?: string, xAxisName?: string, yAxisName?: string, xAxisDataTypeName?: string, yAxisDataTypeName?: string) {
    this.request = request;
    this.data = data;

    this.subjectName = subjectName;
    this.title = title;

    this.xAxisName = xAxisName;
    this.yAxisName = yAxisName;
    
    this.xAxisDataTypeName = xAxisDataTypeName;
    this.yAxisDataTypeName = yAxisDataTypeName;
  }

  buildGraphData(xLabelMapping: Map<string, string>, yLabelMapping: Map<string, string>, yColorMapping: Map<string, string>) {
    // Group by y (status), and then x (companies, themes etc)
    var groupedByY: { [yValue: string]: { [xValue: string]: number } } = {};
    var allY = new Map<string, string>();
    var allX = new Map<string, string>();

    for (var entry of this.data!) {
      var x = entry.x.toLowerCase();
      var y = entry.y.toLowerCase();
      if (!groupedByY[y]) {
        groupedByY[y] = {};
      }
      groupedByY[y][x] = entry.count!;
      allY.set(y, yLabelMapping.get(y) ?? y);
      allX.set(x, xLabelMapping.get(x) ?? x);
    }

    this.xKeyLookup = Array.from(allX);
    this.yKeyLookup = Array.from(allY);

    // X-axis labels (companies, themes etc)
    this.barChartLabels = Array.from(allX.values());

    // Each dataset is a status, values are aligned to each company, theme etc
    this.barChartData = this.yKeyLookup.map(([yKey, yValue]) => {
      var data =  {
        label: yValue,
        data: this.xKeyLookup.map(([xKey, xValue]) => groupedByY[yKey][xKey] || 0),
        stack: 'a',
      } as ChartDataSets;

      var color = yColorMapping.get(yKey);
      if (color) {
        data.backgroundColor = color;
        data.borderColor = 'gray';
        data.hoverBackgroundColor = color;
        data.hoverBorderColor = 'darkgray';
      }

      return data;
    });
  }



}

